// src/utils/statusSheetData.js
// Pure selectors that turn CRM data into Status Sheet section view-models.
// No React, no Firestore — just data in, view-models out (easy to unit-test).

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const sb = (dealtype) =>
  String(dealtype || "").toLowerCase().startsWith("buy") ? "B" : "S";

// A deal's customers are usually contact IDs (resolved via the clientById map),
// but older deals stored inline objects with clientName/name. Handle both.
const clientOf = (d, clientById) => {
  const names = (Array.isArray(d?.customers) ? d.customers : []).map((c) => {
    if (!c) return "";
    if (typeof c === "string") return (clientById && clientById[c]) || "";
    return (
      c.clientName || c.name || (c.id && clientById && clientById[c.id]) || ""
    );
  });
  // A deal can carry both an old inline customer and the same person as a new
  // contact id — de-dupe so names don't appear twice.
  const seen = new Set();
  const uniq = [];
  for (const n of names) {
    const name = String(n || "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    uniq.push(name);
  }
  return uniq.join(", ") || d?.contactName || "";
};

export const isOpen = (d) => {
  const s = String(d?.dealStage || "").toLowerCase();
  return s && s !== "closed" && s !== "cancelled" && s !== "expired";
};

// Calendar dates ("2026-06-10") must be read in LOCAL (Central) time — the
// native Date parser reads them as UTC midnight, which displays/groups as the
// previous day in CST. Full timestamps still parse normally.
export const parseLocalDate = (s) => {
  if (!s) return null;
  if (typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.trim())) {
    const [y, m, d] = s.trim().split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt;
};

const monthKey = (dateStr) => {
  const dt = parseLocalDate(dateStr);
  if (!dt) return null;
  return {
    label: `${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`,
    ord: dt.getTime(),
  };
};

const activeLead = (x) => String(x?.status || "active").toLowerCase() !== "lost";

// A lead that already became a listing/deal is RESOLVED — it must not show as a
// lead anywhere on the sheet. "won"/"converted" are set by the convert flows;
// a convertedDealId with a stale status counts too (belt and braces).
const RESOLVED_LEAD_STATUSES = ["won", "lost", "converted"];
const unresolvedLead = (x) =>
  !RESOLVED_LEAD_STATUSES.includes(String(x?.status || "").toLowerCase()) &&
  !x?.convertedDealId;

// Soft-deleted records (a deletedAt timestamp or deleted: true) never appear.
export const notDeleted = (x) => !x?.deletedAt && x?.deleted !== true;

// Address identity for de-duplication: the street line (before the first
// comma), lowercased, punctuation collapsed. Enough to say "this lead's
// property is already ON the board" without tripping over city/ZIP spelling
// differences ("St Paul" vs "Saint Paul", doubled city suffixes, etc.).
export const normalizeAddressKey = (s) => {
  const line = String(s || "").split(",")[0].toLowerCase();
  return line.replace(/[^a-z0-9]+/g, " ").trim();
};

// Active-listing stages, in display order. Sold (closed) and lost (cancelled/
// expired) listings are NOT here, so they drop off the sheet automatically.
const LISTING_STAGES = ["pre-market", "coming soon", "active", "on hold"];
const LISTING_STAGE_LABELS = {
  "pre-market": "Pre-Market",
  "coming soon": "Coming Soon",
  active: "Active",
  "on hold": "On Hold",
};
const titleCase = (s) =>
  String(s || "")
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");

// Who a deal belongs to: the agent on the deal (agentData) — or, when no
// agent is set, it falls to its team lead (assignedteamlead). Many of the
// team lead's own deals carry only the teamlead field.
export const ownedBy = (d, agentId) =>
  !agentId ||
  d?.agentData === agentId ||
  (!d?.agentData && d?.assignedteamlead === agentId);

// Section A — Closings: open deals with a close date, grouped + sorted by month.
export function getClosings(deals, { agentId, minTime, clientById } = {}) {
  const rows = (deals || [])
    .filter(
      (d) => isOpen(d) && notDeleted(d) && d?.closingdate && monthKey(d.closingdate),
    )
    .filter(
      (d) => !minTime || parseLocalDate(d.closingdate).getTime() >= minTime,
    )
    .filter((d) => ownedBy(d, agentId))
    .map((d) => ({
      dealId: d.createdat || d.id,
      closingDate: d.closingdate,
      address: d.propertyaddress || "",
      sb: sb(d.dealtype),
      client: clientOf(d, clientById),
      salePrice: Number(d.offerPrice || 0),
      // The sheet's own quick note — statusNotes lives on the deal but is
      // separate from the detail page's `notes` field.
      sheetNote: d.statusNotes || "",
      called: !!d.statusCalled, // Phase 2
      _ord: parseLocalDate(d.closingdate).getTime(),
      _month: monthKey(d.closingdate).label,
    }))
    .sort((a, b) => a._ord - b._ord);

  const groups = [];
  for (const r of rows) {
    let g = groups.find((x) => x.month === r._month);
    if (!g) {
      g = { month: r._month, rows: [] };
      groups.push(g);
    }
    g.rows.push(r);
  }
  return groups;
}

// Section B — Active Listings: listing-stage deals (+ active listing leads),
// grouped by status. Sold and cancelled/expired listings drop off the sheet.
export function getListings(
  listingLeads,
  listingDeals,
  { agentId, clientById, nowYear } = {},
) {
  // Deals in an active-listing stage → rows tagged with that stage. Sold
  // (closed) and lost (cancelled/expired) aren't listing stages, so excluded.
  const fromDeals = (listingDeals || [])
    .filter(notDeleted)
    .filter((d) =>
      LISTING_STAGES.includes(String(d.dealStage || "").toLowerCase()),
    )
    .filter((d) => ownedBy(d, agentId))
    .map((d) => ({
      dealId: d.createdat || d.id,
      stageKey: String(d.dealStage || "").toLowerCase(),
      address: d.propertyaddress || "",
      client: clientOf(d, clientById),
      // Listing deals carry their numbers under listing-specific fields.
      price: Number(d.listPrice || d.suggestedPrice || d.offerPrice || 0),
      exp: d.expirationdate || "",
      assigned: d.agentName || d.assignedAgentName || "",
      // The sheet's own quick note — separate from the deal's `notes`.
      sheetNote: d.statusNotes || "",
      // When does it hit the market? Live Date first, then the deal page's
      // Target/Coming Soon date (stored as listingAppointment), then the
      // listing-request form's listing date.
      listDate: d.targetListDate || d.listingAppointment || d.listingDate || "",
    }));
  // Active listing leads (won/lost are resolved outcomes → excluded). Leads
  // still working the pipeline board (incl. Deferred / Next Year parking)
  // aren't listings yet — they stay off this report.
  const pipelineStages = [
    "new lead",
    "contacted",
    "appt set",
    "appt held",
    "deferred",
    "next year",
  ];
  const fromLeads = (listingLeads || [])
    .filter((l) => activeLead(l) && notDeleted(l))
    .filter(
      (l) =>
        !["won", "lost", "converted"].includes(
          String(l.status || "").toLowerCase(),
        ),
    )
    .filter(
      (l) => !pipelineStages.includes(String(l.stage || "").toLowerCase()),
    )
    .filter((l) => !agentId || l.assignedAgent === agentId)
    .map((l) => ({
      leadId: l.id,
      stageKey: String(l.status || "active").toLowerCase(),
      address: l.propertyAddress || "",
      client: l.contactName || "",
      price: Number(l.listPrice || l.estValue || 0),
      exp: l.nextFollowUp || l.expDate || "",
      assigned: l.assignedAgentName || "",
      sheetNote: l.statusNotes || "",
    }));

  // Group by status/stage — "categorized by status". Pre-Market homes whose
  // listing date falls in a FUTURE year split into their own section
  // ("Pre-Market — 2027") so next year's inventory reads separately.
  const thisYear = nowYear || new Date().getFullYear();
  const groups = [];
  for (const r of [...fromDeals, ...fromLeads]) {
    let key = r.stageKey;
    let label = LISTING_STAGE_LABELS[key] || titleCase(key);
    if (key === "pre-market" && r.listDate) {
      const y = parseLocalDate(r.listDate)?.getFullYear();
      if (y && y > thisYear) {
        key = `pre-market-${y}`;
        label = `Pre-Market — ${y}`;
      }
    }
    let g = groups.find((x) => x.stageKey === key);
    if (!g) {
      g = { stageKey: key, status: label, rows: [] };
      groups.push(g);
    }
    g.rows.push(r);
  }
  const ord = (g) => {
    const future = /^pre-market-(\d{4})$/.exec(g.stageKey);
    if (future) return Number(future[1]) / 100000; // right after Pre-Market, by year
    const i = LISTING_STAGES.indexOf(g.stageKey);
    return i === -1 ? 99 : i;
  };
  return groups.sort((a, b) => ord(a) - ord(b));
}

// Section C — Agent Activity: active deals grouped by agent.
export function getAgentActivity(
  deals,
  listingLeads,
  { agentId, nameById, clientById } = {},
) {
  const rows = (deals || [])
    .filter((d) => isOpen(d) && notDeleted(d))
    .filter((d) => !agentId || d.agentData === agentId)
    .map((d) => ({
      agent:
        (nameById && nameById[d.agentData]) ||
        d.assignedAgentName ||
        "Unassigned",
      address: d.propertyaddress || "",
      client: clientOf(d, clientById),
      sb: sb(d.dealtype),
      price: Number(d.offerPrice || 0),
      notes: d.statusNotes || "",
    }));
  const groups = [];
  for (const r of rows) {
    let g = groups.find((x) => x.agent === r.agent);
    if (!g) {
      g = { agent: r.agent, rows: [] };
      groups.push(g);
    }
    g.rows.push(r);
  }
  return groups.sort((a, b) => a.agent.localeCompare(b.agent));
}

// Section D — Listing Appointments.
export function getAppointments(appts, { agentId, boardKeys } = {}) {
  // Won = converted (lives in listings/pending now). Lost = dead. Deferred =
  // back on the Listing Pipeline, not this report. What remains here:
  //   open     → status "pending"  → Listing Appointments (Unconverted)
  //   nextYear → status "next-year" → its own table
  const row = (a) => ({
    dealId: a.dealId || "",
    date: a.appointmentDate || "",
    address: a.address || a.propertyAddress || "",
    client: a.client || a.contactName || a.prospectName || a.name || "",
    phone: a.phone || a.phoneNumber || "",
  });
  const mine = (appts || []).filter((a) => !agentId || a.agentId === agentId);
  const byStatus = (s) =>
    mine.filter((a) => String(a.status || "pending").toLowerCase() === s);
  // An "unconverted" appointment whose property is already ON the board is
  // actually converted — someone just never flipped it to won. Hide it here
  // (display dedup); the Archive Converted Leads cleanup fixes the record.
  const notOnBoard = (a) => {
    if (!(boardKeys instanceof Set) || boardKeys.size === 0) return true;
    const key = normalizeAddressKey(a.address || a.propertyAddress || "");
    return !key || !boardKeys.has(key);
  };
  return {
    open: byStatus("pending").filter(notOnBoard).map(row),
    // Next Year stays as-is even if the property is on the board — an On Hold
    // listing parked with a next-year appointment is intentional tracking.
    nextYear: byStatus("next-year").map(row),
  };
}

// Section E — Buyers & Leads by tier (A/B/C + ungraded).
export function getLeadsByTier(buyerLeads, listingLeads, { agentId, boardKeys } = {}) {
  // A lead whose property is already a board listing is converted in all but
  // paperwork — hide it (the Archive Converted Leads cleanup fixes the record).
  const notOnBoard = (x) => {
    if (!(boardKeys instanceof Set) || boardKeys.size === 0) return true;
    const key = normalizeAddressKey(x.propertyAddress || "");
    return !key || !boardKeys.has(key);
  };
  const all = [...(buyerLeads || []), ...(listingLeads || [])].filter(
    (x) =>
      activeLead(x) &&
      unresolvedLead(x) &&
      notDeleted(x) &&
      notOnBoard(x) &&
      (!agentId || x.assignedAgent === agentId),
  );
  const out = { A: [], B: [], C: [], ungraded: [] };
  for (const l of all) {
    const r = String(l.rating || "").toUpperCase();
    const row = { name: l.contactName || "", stage: l.stage || "" };
    if (r === "A" || r === "B" || r === "C") out[r].push(row);
    else out.ungraded.push(row);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Every address that is already ON the board: real deals (any stage, excluding
// file-request stubs — those are a lead's own linked file, not a conversion)
// plus listing leads displayed as listings (Pre-Market/Coming Soon/Active/
// On Hold). Used both for display dedup and by the cleanup planner below.
export function boardAddressKeys(deals, listingLeads) {
  const keys = new Set();
  (deals || [])
    .filter(notDeleted)
    .filter((d) => String(d.requestType || "") !== "fileRequest")
    .forEach((d) => {
      const k = normalizeAddressKey(d.propertyaddress);
      if (k) keys.add(k);
    });
  (listingLeads || [])
    .filter(notDeleted)
    .filter(
      (l) =>
        activeLead(l) &&
        !RESOLVED_LEAD_STATUSES.includes(String(l?.status || "").toLowerCase()),
    )
    .filter((l) => LISTING_STAGES.includes(String(l.stage || "").toLowerCase()))
    .forEach((l) => {
      const k = normalizeAddressKey(l.propertyAddress);
      if (k) keys.add(k);
    });
  return keys;
}

// ---------------------------------------------------------------------------
// Cleanup planner — powers the admin "Archive Converted Leads" button.
// Pure: data in, a plan out. NOTHING is written here; the caller shows the
// plan to the admin (counts + names) and only then applies it. Every action
// is a recoverable status flip — never a delete.
//
// A lead/appointment counts as "converted but never archived" when the
// property it's about is already ON the board:
//   - a real (non-deleted, non-file-request-stub) deal at the same address, or
//   - a listing lead already displayed as a listing (Pre-Market/Coming Soon/
//     Active/On Hold) at the same address.
// Buyer leads have no address — they match when their contact is a client on
// a real BUYER-side deal (their turn-in), or they carry a convertedDealId the
// status never caught up with.
export function planStatusCleanup({
  deals,
  listingLeads,
  buyerLeads,
  appointments,
} = {}) {
  const resolved = (l) =>
    RESOLVED_LEAD_STATUSES.includes(String(l?.status || "").toLowerCase());

  // Real board deals only — a file-request stub is the lead's OWN linked file,
  // not evidence the lead converted.
  const liveDeals = (deals || [])
    .filter(notDeleted)
    .filter((d) => String(d.requestType || "") !== "fileRequest");
  const boardKeys = boardAddressKeys(deals, listingLeads);

  // Contacts who are clients on a real buyer-side deal (turn-ins).
  const buyerDealContactIds = new Set();
  liveDeals
    .filter((d) => String(d.dealtype || "").toLowerCase().startsWith("buy"))
    .forEach((d) =>
      (Array.isArray(d.customers) ? d.customers : []).forEach((c) => {
        const id = typeof c === "string" ? c : c && c.id;
        if (id) buyerDealContactIds.add(String(id));
      }),
    );

  const listingLeadsToArchive = (listingLeads || [])
    .filter(notDeleted)
    .filter((l) => activeLead(l) && !resolved(l))
    .filter((l) => {
      if (l.convertedDealId) return true; // linked, status never caught up
      // Leads DISPLAYED as listings are the listing — never archive those.
      if (LISTING_STAGES.includes(String(l.stage || "").toLowerCase()))
        return false;
      const key = normalizeAddressKey(l.propertyAddress);
      return !!key && boardKeys.has(key);
    })
    .map((l) => ({
      id: l.id,
      label: [l.contactName || "?", l.propertyAddress || ""].filter(Boolean).join(" — "),
    }));

  const buyerLeadsToArchive = (buyerLeads || [])
    .filter(notDeleted)
    .filter((l) => activeLead(l) && !resolved(l))
    .filter(
      (l) =>
        !!l.convertedDealId ||
        (l.contactId && buyerDealContactIds.has(String(l.contactId))),
    )
    .map((l) => ({ id: l.id, label: l.contactName || "?" }));

  const appointmentsToWin = (appointments || [])
    .filter((a) => String(a.status || "pending").toLowerCase() === "pending")
    .filter((a) => {
      const key = normalizeAddressKey(a.address || a.propertyAddress || "");
      return !!key && boardKeys.has(key);
    })
    .map((a) => ({
      id: a.id,
      label: [a.client || a.contactName || "?", a.address || ""].filter(Boolean).join(" — "),
    }));

  return { listingLeadsToArchive, buyerLeadsToArchive, appointmentsToWin };
}
